import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInAnonymously,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  updateProfile,
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
  linkWithCredential
} from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { auth, functions } from './config';
import { upsertUserProfile } from './db';

function normalizeDisplayName(name) {
  return String(name || '').trim().replace(/\s+/g, ' ').slice(0, 40);
}

/** Resolves when an in-flight signup finishes profile writes (or immediately if none). */
let signupGate = Promise.resolve();

export function waitForSignupGate() {
  return signupGate;
}

export const signUp = async (email, password, displayName) => {
  const name = normalizeDisplayName(displayName);
  if (!name) {
    const err = new Error('Please enter your name.');
    err.code = 'auth/missing-name';
    throw err;
  }

  let releaseGate = () => {};
  signupGate = new Promise((resolve) => {
    releaseGate = resolve;
  });

  try {
    // If the visitor is browsing as a guest, upgrade the anonymous session in
    // place (same uid) so their current conversation survives account creation.
    let user;
    const current = auth.currentUser;
    if (current && current.isAnonymous) {
      const credential = EmailAuthProvider.credential(email, password);
      const result = await linkWithCredential(current, credential);
      user = result.user;
    } else {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      user = userCredential.user;
    }
    await updateProfile(user, { displayName: name });
    await upsertUserProfile(user.uid, {
      displayName: name,
      email: user.email,
    });
    await user.reload();
    return auth.currentUser;
  } finally {
    releaseGate();
    signupGate = Promise.resolve();
  }
};

/**
 * Signing in to an account someone already has. From a guest session, what
 * the server kept for the guest (the conversations the team keeps, a note to
 * Rob, an email-list sign-up) then moves to the account, so deleting the
 * account deletes it too, and the guest login goes (claimGuestSession,
 * functions/account.js). The guest's own token, read before the sign-in, is
 * what proves the guest session was this one's. It never stands in the way:
 * it runs after the sign-in, unawaited, and a failure leaves the guest's
 * conversations where they were.
 */
export const signIn = async (email, password) => {
  const guest = auth.currentUser && auth.currentUser.isAnonymous ? auth.currentUser : null;
  let guestToken = null;
  if (guest) {
    try {
      guestToken = await guest.getIdToken();
    } catch (e) {
      guestToken = null; // offline, or the guest session is gone: nothing to hand over
    }
  }
  const userCredential = await signInWithEmailAndPassword(auth, email, password);
  if (guestToken && userCredential.user && userCredential.user.uid !== guest.uid) {
    Promise.resolve()
      .then(() => httpsCallable(functions, 'claimGuestSession')({ guestToken }))
      .catch(() => {});
  }
  return userCredential.user;
};

/**
 * Guest entry: anonymous Firebase session so anyone can talk to Rob with no
 * signup wall, while every visitor still has a UID the backend can rate-limit.
 * Guest chats can't be saved; signing in unlocks saving and higher limits.
 */
export const signInAsGuest = async () => {
  const userCredential = await signInAnonymously(auth);
  return userCredential.user;
};

export const logOut = async () => {
  await signOut(auth);
};

export const onAuthChange = (callback) => {
  return onAuthStateChanged(auth, callback);
};

export const resetPassword = async (email) => {
  await sendPasswordResetEmail(auth, email);
};

export const updateUserDisplayName = async (displayName) => {
  const user = auth.currentUser;
  if (!user) {
    const err = new Error('Not signed in');
    err.code = 'auth/user-not-found';
    throw err;
  }

  const name = normalizeDisplayName(displayName);
  if (!name) {
    const err = new Error('Please enter your name.');
    err.code = 'auth/missing-name';
    throw err;
  }

  await updateProfile(user, { displayName: name });
  await upsertUserProfile(user.uid, {
    displayName: name,
    email: user.email,
  });
  await user.reload();
  return auth.currentUser;
};

/** Re-proves identity with the current password (Firebase requires a recent sign-in for sensitive changes). */
async function reauthenticate(currentPassword) {
  const user = auth.currentUser;
  if (!user || !user.email) {
    const err = new Error('Not signed in');
    err.code = 'auth/user-not-found';
    throw err;
  }
  const credential = EmailAuthProvider.credential(user.email, currentPassword);
  await reauthenticateWithCredential(user, credential);
  return user;
}

export const changePassword = async (currentPassword, newPassword) => {
  if (!newPassword || newPassword.length < 6) {
    const err = new Error('Use a password with at least 6 characters.');
    err.code = 'auth/weak-password';
    throw err;
  }
  const user = await reauthenticate(currentPassword);
  await updatePassword(user, newPassword);
};

/**
 * Erases the account and everything stored under it (saved chats, profile,
 * counters, role) via the deleteMyAccount function, then ends the session.
 */
export const deleteAccount = async (currentPassword) => {
  await reauthenticate(currentPassword);
  const call = httpsCallable(functions, 'deleteMyAccount');
  await call({});
  // The Auth user is gone server-side; clear the local session too.
  try { await signOut(auth); } catch (e) { /* already signed out */ }
};
